import { useMemo } from "react";
import { 
  type CountryCode, 
  type PayPeriod, 
  type TaxCalculationResult,
  type TaxBreakdownItem,
  type CountryComparisonResult,
  countries,
  payPeriodMultipliers,
  usStates
} from "@shared/schema";

const CHART_COLORS = {
  income: "hsl(217 91% 60%)",
  social: "hsl(142 71% 45%)",
  state: "hsl(262 83% 58%)",
  other: "hsl(43 96% 56%)",
  net: "hsl(142 71% 45%)"
};

function calculateUKTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const personalAllowance = annualSalary > 125140 ? 0 : 
    annualSalary > 100000 ? Math.max(0, 12570 - (annualSalary - 100000) / 2) : 12570;

  const taxableIncome = Math.max(0, annualSalary - personalAllowance);
  let incomeTax = 0;

  if (taxableIncome > 0) {
    const basicBand = Math.min(taxableIncome, 37700);
    incomeTax += basicBand * 0.20;

    if (taxableIncome > 37700) {
      const higherBand = Math.min(taxableIncome - 37700, 87440);
      incomeTax += higherBand * 0.40;
    }

    if (taxableIncome > 125140) {
      const additionalBand = taxableIncome - 125140;
      incomeTax += additionalBand * 0.45;
    }
  }

  breakdown.push({
    name: "Income Tax",
    amount: incomeTax,
    percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.income
  });
  totalTax += incomeTax;

  let ni = 0;
  const weeklyEarnings = annualSalary / 52;
  const primaryThreshold = 242;
  const upperLimit = 967;

  if (weeklyEarnings > primaryThreshold) {
    const mainNI = Math.min(weeklyEarnings - primaryThreshold, upperLimit - primaryThreshold) * 0.08;
    ni += mainNI * 52;

    if (weeklyEarnings > upperLimit) {
      ni += (weeklyEarnings - upperLimit) * 0.02 * 52;
    }
  }

  breakdown.push({
    name: "National Insurance",
    amount: ni,
    percentage: annualSalary > 0 ? (ni / annualSalary) * 100 : 0,
    color: CHART_COLORS.social
  });
  totalTax += ni;

  return { breakdown, totalTax };
}

function calculateUSTax(annualSalary: number, stateCode: string): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const standardDeduction = 15750;
  const taxableIncome = Math.max(0, annualSalary - standardDeduction);

  const brackets = [
    { limit: 11925, rate: 0.10 },
    { limit: 48475, rate: 0.12 },
    { limit: 103350, rate: 0.22 },
    { limit: 197300, rate: 0.24 },
    { limit: 250525, rate: 0.32 },
    { limit: 626350, rate: 0.35 },
    { limit: Infinity, rate: 0.37 }
  ];

  let federalTax = 0;
  let previousLimit = 0;

  for (const bracket of brackets) {
    if (taxableIncome > previousLimit) {
      const taxableAtBracket = Math.min(taxableIncome, bracket.limit) - previousLimit;
      federalTax += taxableAtBracket * bracket.rate;
      previousLimit = bracket.limit;
    } else {
      break;
    }
  }

  breakdown.push({
    name: "Federal Income Tax",
    amount: federalTax,
    percentage: annualSalary > 0 ? (federalTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.income
  });
  totalTax += federalTax;

  const state = usStates.find(s => s.code === stateCode);
  const stateRate = state?.taxRate || 0;
  const stateTax = annualSalary * (stateRate / 100);

  if (stateTax > 0) {
    breakdown.push({
      name: "State Tax",
      amount: stateTax,
      percentage: stateRate,
      color: CHART_COLORS.state
    });
    totalTax += stateTax;
  }

  const socialSecurityWageBase = 176100;
  const socialSecurity = Math.min(annualSalary, socialSecurityWageBase) * 0.062;
  breakdown.push({
    name: "Social Security",
    amount: socialSecurity,
    percentage: annualSalary > 0 ? (socialSecurity / annualSalary) * 100 : 0,
    color: CHART_COLORS.social
  });
  totalTax += socialSecurity;

  const medicare = annualSalary * 0.0145;
  breakdown.push({
    name: "Medicare",
    amount: medicare,
    percentage: 1.45,
    color: CHART_COLORS.other
  });
  totalTax += medicare;

  return { breakdown, totalTax };
}

function calculateCanadaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const basicPersonalAmount = 16129;
  const taxableIncome = Math.max(0, annualSalary - basicPersonalAmount);

  const brackets = [
    { limit: 57375, rate: 0.15 },
    { limit: 114750, rate: 0.205 },
    { limit: 177882, rate: 0.26 },
    { limit: 253414, rate: 0.29 },
    { limit: Infinity, rate: 0.33 }
  ];

  let federalTax = 0;
  let previousLimit = 0;

  for (const bracket of brackets) {
    if (taxableIncome > previousLimit) {
      const taxableAtBracket = Math.min(taxableIncome, bracket.limit) - previousLimit;
      federalTax += taxableAtBracket * bracket.rate;
      previousLimit = bracket.limit;
    } else {
      break;
    }
  }

  breakdown.push({
    name: "Federal Income Tax",
    amount: federalTax,
    percentage: annualSalary > 0 ? (federalTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.income
  });
  totalTax += federalTax;

  const provincialTax = annualSalary * 0.0505;
  breakdown.push({
    name: "Provincial Tax (Ontario)",
    amount: provincialTax,
    percentage: 5.05,
    color: CHART_COLORS.state
  });
  totalTax += provincialTax;

  const cpp = Math.min(annualSalary * 0.0595, 4034);
  breakdown.push({
    name: "CPP",
    amount: cpp,
    percentage: annualSalary > 0 ? (cpp / annualSalary) * 100 : 0,
    color: CHART_COLORS.social
  });
  totalTax += cpp;

  const ei = Math.min(annualSalary * 0.0163, 1049);
  breakdown.push({
    name: "EI",
    amount: ei,
    percentage: annualSalary > 0 ? (ei / annualSalary) * 100 : 0,
    color: CHART_COLORS.other
  });
  totalTax += ei;

  return { breakdown, totalTax };
}

function calculateAustraliaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let incomeTax = 0;
  if (annualSalary <= 18200) {
    incomeTax = 0;
  } else if (annualSalary <= 45000) {
    incomeTax = (annualSalary - 18200) * 0.16;
  } else if (annualSalary <= 135000) {
    incomeTax = 4288 + (annualSalary - 45000) * 0.30;
  } else if (annualSalary <= 190000) {
    incomeTax = 31288 + (annualSalary - 135000) * 0.37;
  } else {
    incomeTax = 51638 + (annualSalary - 190000) * 0.45;
  }

  breakdown.push({
    name: "Income Tax",
    amount: incomeTax,
    percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.income
  });
  totalTax += incomeTax;

  const medicareLevy = annualSalary * 0.02;
  breakdown.push({
    name: "Medicare Levy",
    amount: medicareLevy,
    percentage: 2,
    color: CHART_COLORS.social
  });
  totalTax += medicareLevy;

  return { breakdown, totalTax };
}

function calculateGermanyTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let incomeTax = 0;
  const basicAllowance = 12096;

  if (annualSalary <= basicAllowance) {
    incomeTax = 0;
  } else if (annualSalary <= 68480) {
    const y = (annualSalary - basicAllowance) / 10000;
    incomeTax = (922.98 * y + 1400) * y;
  } else if (annualSalary <= 277826) {
    const z = (annualSalary - 68480) / 10000;
    incomeTax = (181.19 * z + 2397) * z + 18307;
  } else {
    incomeTax = 0.45 * annualSalary - 21250;
  }

  breakdown.push({
    name: "Income Tax",
    amount: incomeTax,
    percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.income
  });
  totalTax += incomeTax;

  const solidaritySurcharge = incomeTax > 18130 ? incomeTax * 0.055 : 0;
  if (solidaritySurcharge > 0) {
    breakdown.push({
      name: "Solidarity Surcharge",
      amount: solidaritySurcharge,
      percentage: annualSalary > 0 ? (solidaritySurcharge / annualSalary) * 100 : 0,
      color: CHART_COLORS.other
    });
    totalTax += solidaritySurcharge;
  }

  const socialSecurityCap = 96600;
  const healthInsuranceCap = 66150;

  const pensionInsurance = Math.min(annualSalary, socialSecurityCap) * 0.093;
  breakdown.push({
    name: "Pension Insurance",
    amount: pensionInsurance,
    percentage: annualSalary > 0 ? (pensionInsurance / annualSalary) * 100 : 0,
    color: CHART_COLORS.social
  });
  totalTax += pensionInsurance;

  const unemploymentInsurance = Math.min(annualSalary, socialSecurityCap) * 0.013;
  breakdown.push({
    name: "Unemployment Insurance",
    amount: unemploymentInsurance,
    percentage: annualSalary > 0 ? (unemploymentInsurance / annualSalary) * 100 : 0,
    color: CHART_COLORS.other
  });
  totalTax += unemploymentInsurance;

  const healthInsurance = Math.min(annualSalary, healthInsuranceCap) * 0.073;
  breakdown.push({
    name: "Health Insurance",
    amount: healthInsurance,
    percentage: annualSalary > 0 ? (healthInsurance / annualSalary) * 100 : 0,
    color: CHART_COLORS.state
  });
  totalTax += healthInsurance;

  const nursingCareInsurance = Math.min(annualSalary, healthInsuranceCap) * 0.01875;
  breakdown.push({
    name: "Nursing Care Insurance",
    amount: nursingCareInsurance,
    percentage: annualSalary > 0 ? (nursingCareInsurance / annualSalary) * 100 : 0,
    color: CHART_COLORS.other
  });
  totalTax += nursingCareInsurance;

  return { breakdown, totalTax };
}

function calculateNetherlandsTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // 2025 Netherlands Box 1 tax rates (3 brackets)
  let incomeTax = 0;
  if (annualSalary <= 38441) {
    incomeTax = annualSalary * 0.3582;
  } else if (annualSalary <= 76817) {
    incomeTax = 38441 * 0.3582 + (annualSalary - 38441) * 0.3748;
  } else {
    incomeTax = 38441 * 0.3582 + (76817 - 38441) * 0.3748 + (annualSalary - 76817) * 0.495;
  }

  // General tax credit (algemene heffingskorting) 2025
  // Max €3,362, phases out above €24,812 at 6.57%
  const generalTaxCredit = Math.max(0, 3362 - (annualSalary > 24812 ? (annualSalary - 24812) * 0.0657 : 0));
  
  // Labor tax credit (arbeidskorting) 2025
  // Phase 1: €0-€11,491: 8.231% of income
  // Phase 2: €11,491-€43,071: €5,599 (flat max)
  // Phase 3: €43,071-€132,290: €5,599 - 6.51% * (income - €43,071)
  // Above €132,290: €0
  let laborTaxCredit = 0;
  if (annualSalary <= 11491) {
    laborTaxCredit = annualSalary * 0.08231;
  } else if (annualSalary <= 43071) {
    laborTaxCredit = 5599;
  } else if (annualSalary <= 132290) {
    laborTaxCredit = Math.max(0, 5599 - (annualSalary - 43071) * 0.0651);
  } else {
    laborTaxCredit = 0;
  }
  
  // Apply both tax credits
  const totalCredits = generalTaxCredit + laborTaxCredit;
  incomeTax = Math.max(0, incomeTax - totalCredits);

  breakdown.push({
    name: "Income Tax",
    amount: incomeTax,
    percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.income
  });
  totalTax += incomeTax;

  return { breakdown, totalTax };
}

function calculateUAETax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  return {
    breakdown: [{
      name: "No Income Tax",
      amount: 0,
      percentage: 0,
      color: CHART_COLORS.net
    }],
    totalTax: 0
  };
}

function calculateSingaporeTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // 2025 Singapore IRAS tax rates (YA 2025)
  let incomeTax = 0;
  if (annualSalary <= 20000) {
    incomeTax = 0;
  } else if (annualSalary <= 30000) {
    incomeTax = (annualSalary - 20000) * 0.02;
  } else if (annualSalary <= 40000) {
    incomeTax = 200 + (annualSalary - 30000) * 0.035;
  } else if (annualSalary <= 80000) {
    incomeTax = 550 + (annualSalary - 40000) * 0.07;
  } else if (annualSalary <= 120000) {
    incomeTax = 3350 + (annualSalary - 80000) * 0.115;
  } else if (annualSalary <= 160000) {
    incomeTax = 7950 + (annualSalary - 120000) * 0.15;
  } else if (annualSalary <= 200000) {
    incomeTax = 13950 + (annualSalary - 160000) * 0.18;
  } else if (annualSalary <= 240000) {
    incomeTax = 21150 + (annualSalary - 200000) * 0.19;
  } else if (annualSalary <= 280000) {
    incomeTax = 28750 + (annualSalary - 240000) * 0.195;
  } else if (annualSalary <= 320000) {
    incomeTax = 36550 + (annualSalary - 280000) * 0.20;
  } else if (annualSalary <= 500000) {
    incomeTax = 44550 + (annualSalary - 320000) * 0.22;
  } else if (annualSalary <= 1000000) {
    incomeTax = 84150 + (annualSalary - 500000) * 0.23;
  } else {
    incomeTax = 199150 + (annualSalary - 1000000) * 0.24;
  }

  breakdown.push({
    name: "Income Tax",
    amount: incomeTax,
    percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.income
  });
  totalTax += incomeTax;

  const cpf = Math.min(annualSalary * 0.20, 20400);
  breakdown.push({
    name: "CPF (Employee)",
    amount: cpf,
    percentage: annualSalary > 0 ? (cpf / annualSalary) * 100 : 0,
    color: CHART_COLORS.social
  });
  totalTax += cpf;

  return { breakdown, totalTax };
}

function calculateSwitzerlandTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const federalTax = annualSalary * 0.115;
  breakdown.push({
    name: "Federal Tax",
    amount: federalTax,
    percentage: annualSalary > 0 ? (federalTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.income
  });
  totalTax += federalTax;

  const cantonalTax = annualSalary * 0.12;
  breakdown.push({
    name: "Cantonal Tax (Zurich)",
    amount: cantonalTax,
    percentage: annualSalary > 0 ? (cantonalTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.state
  });
  totalTax += cantonalTax;

  const ahv = annualSalary * 0.0525;
  breakdown.push({
    name: "AHV/IV/EO",
    amount: ahv,
    percentage: 5.25,
    color: CHART_COLORS.social
  });
  totalTax += ahv;

  return { breakdown, totalTax };
}

function calculateIrelandTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // 2025 Ireland tax rates (Revenue)
  let incomeTax = 0;
  const standardRateCutoff = 44000; // Updated for 2025
  const personalTaxCredit = 1775; // 2025 Personal Tax Credit
  const payeCredit = 2000; // 2025 PAYE Employee Credit
  const taxCredits = personalTaxCredit + payeCredit;

  if (annualSalary <= standardRateCutoff) {
    incomeTax = annualSalary * 0.20;
  } else {
    incomeTax = standardRateCutoff * 0.20 + (annualSalary - standardRateCutoff) * 0.40;
  }
  incomeTax = Math.max(0, incomeTax - taxCredits);

  breakdown.push({
    name: "Income Tax",
    amount: incomeTax,
    percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0,
    color: CHART_COLORS.income
  });
  totalTax += incomeTax;

  // PRSI 2025: 4.2%
  const prsi = annualSalary * 0.042;
  breakdown.push({
    name: "PRSI",
    amount: prsi,
    percentage: 4.2,
    color: CHART_COLORS.social
  });
  totalTax += prsi;

  // USC 2025 rates
  let usc = 0;
  if (annualSalary > 13000) {
    // First €12,012 at 0.5%
    usc = Math.min(annualSalary, 12012) * 0.005;
    // €12,013 - €27,382 at 2%
    if (annualSalary > 12012) {
      usc += Math.min(annualSalary - 12012, 15370) * 0.02;
    }
    // €27,383 - €70,044 at 3%
    if (annualSalary > 27382) {
      usc += Math.min(annualSalary - 27382, 42662) * 0.03;
    }
    // Above €70,044 at 8%
    if (annualSalary > 70044) {
      usc += (annualSalary - 70044) * 0.08;
    }
  }

  breakdown.push({
    name: "USC",
    amount: usc,
    percentage: annualSalary > 0 ? (usc / annualSalary) * 100 : 0,
    color: CHART_COLORS.other
  });
  totalTax += usc;

  return { breakdown, totalTax };
}

function calculateFranceTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // 10% frais professionnels deduction (min €472, max €13,522)
  const abattement = Math.min(Math.max(annualSalary * 0.10, 472), 13522);
  const netImposable = Math.max(0, annualSalary - abattement);

  let incomeTax = 0;
  if (netImposable <= 11497) {
    incomeTax = 0;
  } else if (netImposable <= 29315) {
    incomeTax = (netImposable - 11497) * 0.11;
  } else if (netImposable <= 83823) {
    incomeTax = (29315 - 11497) * 0.11 + (netImposable - 29315) * 0.30;
  } else if (netImposable <= 180294) {
    incomeTax = (29315 - 11497) * 0.11 + (83823 - 29315) * 0.30 + (netImposable - 83823) * 0.41;
  } else {
    incomeTax = (29315 - 11497) * 0.11 + (83823 - 29315) * 0.30 + (180294 - 83823) * 0.41 + (netImposable - 180294) * 0.45;
  }

  // Décote (soft reduction for low-income single filers, 2025)
  if (incomeTax > 0 && incomeTax < 1929) {
    incomeTax = Math.max(0, 1.75 * incomeTax - 1929);
  }

  breakdown.push({ name: "Income Tax (IR)", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  // CSG 9.2% + CRDS 0.5% = 9.7% on 98.25% of gross
  const csgCrds = annualSalary * 0.9825 * 0.097;
  breakdown.push({ name: "CSG/CRDS", amount: csgCrds, percentage: annualSalary > 0 ? (csgCrds / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += csgCrds;

  // Pension: 6.9% capped at SS plafond (€46,368) + 0.4% uncapped
  const pensionBase = Math.min(annualSalary, 46368) * 0.069 + annualSalary * 0.004;
  breakdown.push({ name: "Pension (Retraite)", amount: pensionBase, percentage: annualSalary > 0 ? (pensionBase / annualSalary) * 100 : 0, color: CHART_COLORS.other });
  totalTax += pensionBase;

  return { breakdown, totalTax };
}

function calculateSpainTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // Mínimo personal €5,550 (personal allowance)
  const taxableIncome = Math.max(0, annualSalary - 5550);

  let irpf = 0;
  if (taxableIncome <= 12450) {
    irpf = taxableIncome * 0.19;
  } else if (taxableIncome <= 20200) {
    irpf = 12450 * 0.19 + (taxableIncome - 12450) * 0.24;
  } else if (taxableIncome <= 35200) {
    irpf = 12450 * 0.19 + 7750 * 0.24 + (taxableIncome - 20200) * 0.30;
  } else if (taxableIncome <= 60000) {
    irpf = 12450 * 0.19 + 7750 * 0.24 + 15000 * 0.30 + (taxableIncome - 35200) * 0.37;
  } else if (taxableIncome <= 300000) {
    irpf = 12450 * 0.19 + 7750 * 0.24 + 15000 * 0.30 + 24800 * 0.37 + (taxableIncome - 60000) * 0.45;
  } else {
    irpf = 12450 * 0.19 + 7750 * 0.24 + 15000 * 0.30 + 24800 * 0.37 + 240000 * 0.45 + (taxableIncome - 300000) * 0.47;
  }

  breakdown.push({ name: "IRPF (Income Tax)", amount: irpf, percentage: annualSalary > 0 ? (irpf / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += irpf;

  // Social Security: 6.35% capped at €58,908/year
  const ssCap = 58908;
  const socialSecurity = Math.min(annualSalary, ssCap) * 0.0635;
  breakdown.push({ name: "Social Security", amount: socialSecurity, percentage: annualSalary > 0 ? (socialSecurity / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += socialSecurity;

  return { breakdown, totalTax };
}

function calculateItalyTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // IRPEF 2025: 0–28k 23%, 28k–50k 35%, above 50k 43%
  let irpef = 0;
  if (annualSalary <= 28000) {
    irpef = annualSalary * 0.23;
  } else if (annualSalary <= 50000) {
    irpef = 28000 * 0.23 + (annualSalary - 28000) * 0.35;
  } else {
    irpef = 28000 * 0.23 + 22000 * 0.35 + (annualSalary - 50000) * 0.43;
  }

  // Detrazione lavoratori dipendenti (employee tax credit)
  let detrazione = 0;
  if (annualSalary <= 28000) {
    detrazione = 1955;
  } else if (annualSalary <= 50000) {
    detrazione = 1190 * (50000 - annualSalary) / 22000;
  } else {
    detrazione = 0;
  }
  irpef = Math.max(0, irpef - detrazione);

  breakdown.push({ name: "IRPEF (Income Tax)", amount: irpef, percentage: annualSalary > 0 ? (irpef / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += irpef;

  // Regional + municipal addizionale (avg 1.5% + 0.2%)
  const addizionale = annualSalary * 0.017;
  breakdown.push({ name: "Regional/Municipal Tax", amount: addizionale, percentage: 1.7, color: CHART_COLORS.state });
  totalTax += addizionale;

  // INPS employee contributions: 9.19% capped at €119,650
  const inps = Math.min(annualSalary, 119650) * 0.0919;
  breakdown.push({ name: "INPS (Social Security)", amount: inps, percentage: annualSalary > 0 ? (inps / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += inps;

  return { breakdown, totalTax };
}

function calculateSwedenTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // Grundavdrag (basic deduction) 2025/2026 — Skatteverket official
  let grundavdrag: number;
  if (annualSalary <= 17000) grundavdrag = 17000;
  else if (annualSalary <= 120400) grundavdrag = 17000 + (annualSalary - 17000) * 0.209;
  else if (annualSalary <= 350000) grundavdrag = 37760;
  else if (annualSalary <= 648500) grundavdrag = 37760 - (annualSalary - 350000) / (648500 - 350000) * (37760 - 13100);
  else grundavdrag = 13100;

  const taxableIncome = Math.max(0, annualSalary - grundavdrag);

  // Kommunalskatt (municipal + regional): national avg 32.38% (2026)
  const municipalRate = 0.3238;
  let kommunalskatt = taxableIncome * municipalRate;

  // Jobbskatteavdrag (earned income tax credit) 2025/2026 — reduces kommunalskatt
  // Official piecewise formula from Skatteverket SKV 433
  let jobbskatteavdrag = 0;
  if (taxableIncome > 0) {
    if (taxableIncome <= 20400) {
      jobbskatteavdrag = taxableIncome * 0.3238; // 100% of municipal tax up to 20,400
    } else if (taxableIncome <= 33900) {
      jobbskatteavdrag = 6607 + (taxableIncome - 20400) * 0.283; // phase 1
    } else if (taxableIncome <= 100000) {
      jobbskatteavdrag = 10424 + (taxableIncome - 33900) * 0.055; // phase 2
    } else if (taxableIncome <= 300000) {
      jobbskatteavdrag = 14060 + (taxableIncome - 100000) * 0.015; // phase 3
    } else if (taxableIncome <= 643000) {
      jobbskatteavdrag = 17060 + (taxableIncome - 300000) * 0.005; // phase 4
    } else {
      jobbskatteavdrag = 18775; // maxed out
    }
    // Cap at kommunalskatt (can't get refund)
    jobbskatteavdrag = Math.min(jobbskatteavdrag, kommunalskatt);
  }

  kommunalskatt = Math.max(0, kommunalskatt - jobbskatteavdrag);

  if (kommunalskatt > 0) {
    breakdown.push({ name: "Municipal Income Tax", amount: kommunalskatt, percentage: annualSalary > 0 ? (kommunalskatt / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  }
  if (jobbskatteavdrag > 0) {
    breakdown.push({ name: "Jobbskatteavdrag (Earned Income Credit)", amount: -jobbskatteavdrag, percentage: annualSalary > 0 ? (-jobbskatteavdrag / annualSalary) * 100 : 0, color: CHART_COLORS.net });
  }
  totalTax += kommunalskatt;

  // Statlig inkomstskatt: 20% on taxable income above 643,100 SEK
  const statligSkatt = Math.max(0, taxableIncome - 643100) * 0.20;
  if (statligSkatt > 0) {
    breakdown.push({ name: "National Income Tax", amount: statligSkatt, percentage: annualSalary > 0 ? (statligSkatt / annualSalary) * 100 : 0, color: CHART_COLORS.state });
    totalTax += statligSkatt;
  }

  return { breakdown, totalTax };
}

function calculateNorwayTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // Minstefradrag: 46% of income (min 31,800 NOK, max 109,950 NOK)
  const minstefradrag = Math.min(Math.max(annualSalary * 0.46, 31800), 109950);
  // Personfradrag: 73,100 NOK
  const personfradrag = 73100;
  const alminneligInntekt = Math.max(0, annualSalary - minstefradrag - personfradrag);

  // Normal tax: 22%
  const normalTax = alminneligInntekt * 0.22;
  breakdown.push({ name: "Income Tax (22%)", amount: normalTax, percentage: annualSalary > 0 ? (normalTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += normalTax;

  // Trinnskatt (bracket tax on gross)
  let trinnskatt = 0;
  if (annualSalary > 1350000) trinnskatt += (annualSalary - 1350000) * 0.174;
  if (annualSalary > 937900) trinnskatt += (Math.min(annualSalary, 1350000) - 937900) * 0.164;
  if (annualSalary > 670000) trinnskatt += (Math.min(annualSalary, 937900) - 670000) * 0.134;
  if (annualSalary > 292850) trinnskatt += (Math.min(annualSalary, 670000) - 292850) * 0.040;
  if (annualSalary > 208050) trinnskatt += (Math.min(annualSalary, 292850) - 208050) * 0.017;

  if (trinnskatt > 0) {
    breakdown.push({ name: "Bracket Tax (Trinnskatt)", amount: trinnskatt, percentage: annualSalary > 0 ? (trinnskatt / annualSalary) * 100 : 0, color: CHART_COLORS.state });
    totalTax += trinnskatt;
  }

  // Trygdeavgift (national insurance): 7.8%
  const trygdeavgift = annualSalary * 0.078;
  breakdown.push({ name: "National Insurance (Trygdeavgift)", amount: trygdeavgift, percentage: 7.8, color: CHART_COLORS.social });
  totalTax += trygdeavgift;

  return { breakdown, totalTax };
}

function calculateDenmarkTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // AM-bidrag (labor market contribution): 8%
  const amBidrag = annualSalary * 0.08;
  breakdown.push({ name: "AM-bidrag (Labor Market)", amount: amBidrag, percentage: 8, color: CHART_COLORS.social });
  totalTax += amBidrag;

  const afterAM = annualSalary * 0.92;

  // Personfradrag: 48,000 DKK
  // Beskæftigelsesfradrag: 10.65% capped at 50,300 DKK
  const beskaefltigelsesfradrag = Math.min(afterAM * 0.1065, 50300);
  const taxableIncome = Math.max(0, afterAM - 48000 - beskaefltigelsesfradrag);

  // Bundskat 12.18% + kommuneskat avg 25.12% = 37.3% combined
  const incomeTax = taxableIncome * 0.373;
  breakdown.push({ name: "Income Tax (Bundskat + Kommuneskat)", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  // Topskat: 15% on afterAM income above 588,900 DKK
  const topskat = Math.max(0, afterAM - 588900) * 0.15;
  if (topskat > 0) {
    breakdown.push({ name: "Top Tax (Topskat)", amount: topskat, percentage: annualSalary > 0 ? (topskat / annualSalary) * 100 : 0, color: CHART_COLORS.state });
    totalTax += topskat;
  }

  return { breakdown, totalTax };
}

function calculateNewZealandTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let incomeTax = 0;
  if (annualSalary <= 14000) {
    incomeTax = annualSalary * 0.105;
  } else if (annualSalary <= 48000) {
    incomeTax = 14000 * 0.105 + (annualSalary - 14000) * 0.175;
  } else if (annualSalary <= 70000) {
    incomeTax = 14000 * 0.105 + 34000 * 0.175 + (annualSalary - 48000) * 0.30;
  } else if (annualSalary <= 180000) {
    incomeTax = 14000 * 0.105 + 34000 * 0.175 + 22000 * 0.30 + (annualSalary - 70000) * 0.33;
  } else {
    incomeTax = 14000 * 0.105 + 34000 * 0.175 + 22000 * 0.30 + 110000 * 0.33 + (annualSalary - 180000) * 0.39;
  }

  breakdown.push({ name: "Income Tax", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  // ACC Earner levy: 1.60% capped at NZD 142,283
  const accLevy = Math.min(annualSalary, 142283) * 0.0160;
  breakdown.push({ name: "ACC Earner Levy", amount: accLevy, percentage: annualSalary > 0 ? (accLevy / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += accLevy;

  return { breakdown, totalTax };
}

function calculateJapanTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // Employment income deduction (kyuyo shotoku kojo)
  let employmentDeduction: number;
  if (annualSalary <= 1625000) employmentDeduction = 550000;
  else if (annualSalary <= 1800000) employmentDeduction = annualSalary * 0.40;
  else if (annualSalary <= 3600000) employmentDeduction = annualSalary * 0.30 + 180000;
  else if (annualSalary <= 6600000) employmentDeduction = annualSalary * 0.20 + 540000;
  else if (annualSalary <= 8500000) employmentDeduction = annualSalary * 0.10 + 1200000;
  else employmentDeduction = 1950000;

  const taxableIncome = Math.max(0, annualSalary - employmentDeduction - 480000);

  // National income tax brackets
  let nationalTax = 0;
  if (taxableIncome <= 1950000) nationalTax = taxableIncome * 0.05;
  else if (taxableIncome <= 3300000) nationalTax = 97500 + (taxableIncome - 1950000) * 0.10;
  else if (taxableIncome <= 6950000) nationalTax = 232500 + (taxableIncome - 3300000) * 0.20;
  else if (taxableIncome <= 9000000) nationalTax = 962500 + (taxableIncome - 6950000) * 0.23;
  else if (taxableIncome <= 18000000) nationalTax = 1434000 + (taxableIncome - 9000000) * 0.33;
  else if (taxableIncome <= 40000000) nationalTax = 4404000 + (taxableIncome - 18000000) * 0.40;
  else nationalTax = 13204000 + (taxableIncome - 40000000) * 0.45;

  // Reconstruction special surtax: 2.1% of national income tax
  const reconstructionTax = nationalTax * 0.021;
  const totalNationalTax = nationalTax + reconstructionTax;

  breakdown.push({ name: "National Income Tax", amount: totalNationalTax, percentage: annualSalary > 0 ? (totalNationalTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += totalNationalTax;

  // Residents tax: 10% flat on taxable income
  const residentsTax = taxableIncome * 0.10;
  breakdown.push({ name: "Residents Tax (Juminzei)", amount: residentsTax, percentage: annualSalary > 0 ? (residentsTax / annualSalary) * 100 : 0, color: CHART_COLORS.state });
  totalTax += residentsTax;

  // Social Insurance: Health 4.985% (cap ~16.2M), Pension 9.15% (cap 7.8M), Employment 0.6%
  const healthInsurance = Math.min(annualSalary, 16200000) * 0.04985;
  breakdown.push({ name: "Health Insurance (Kenpo)", amount: healthInsurance, percentage: annualSalary > 0 ? (healthInsurance / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += healthInsurance;

  const pension = Math.min(annualSalary, 7800000) * 0.0915;
  breakdown.push({ name: "Welfare Pension (Kosei Nenkin)", amount: pension, percentage: annualSalary > 0 ? (pension / annualSalary) * 100 : 0, color: CHART_COLORS.other });
  totalTax += pension;

  const employmentInsurance = annualSalary * 0.006;
  breakdown.push({ name: "Employment Insurance", amount: employmentInsurance, percentage: 0.6, color: CHART_COLORS.other });
  totalTax += employmentInsurance;

  return { breakdown, totalTax };
}

function calculatePortugalTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // Dedução específica: €4,462 for employees
  const taxableIncome = Math.max(0, annualSalary - 4462);

  let irs = 0;
  if (taxableIncome <= 7703) {
    irs = taxableIncome * 0.1325;
  } else if (taxableIncome <= 11623) {
    irs = 7703 * 0.1325 + (taxableIncome - 7703) * 0.18;
  } else if (taxableIncome <= 16472) {
    irs = 7703 * 0.1325 + 3920 * 0.18 + (taxableIncome - 11623) * 0.23;
  } else if (taxableIncome <= 21321) {
    irs = 7703 * 0.1325 + 3920 * 0.18 + 4849 * 0.23 + (taxableIncome - 16472) * 0.26;
  } else if (taxableIncome <= 27146) {
    irs = 7703 * 0.1325 + 3920 * 0.18 + 4849 * 0.23 + 4849 * 0.26 + (taxableIncome - 21321) * 0.3275;
  } else if (taxableIncome <= 39791) {
    irs = 7703 * 0.1325 + 3920 * 0.18 + 4849 * 0.23 + 4849 * 0.26 + 5825 * 0.3275 + (taxableIncome - 27146) * 0.37;
  } else if (taxableIncome <= 51997) {
    irs = 7703 * 0.1325 + 3920 * 0.18 + 4849 * 0.23 + 4849 * 0.26 + 5825 * 0.3275 + 12645 * 0.37 + (taxableIncome - 39791) * 0.435;
  } else if (taxableIncome <= 81199) {
    irs = 7703 * 0.1325 + 3920 * 0.18 + 4849 * 0.23 + 4849 * 0.26 + 5825 * 0.3275 + 12645 * 0.37 + 12206 * 0.435 + (taxableIncome - 51997) * 0.45;
  } else {
    irs = 7703 * 0.1325 + 3920 * 0.18 + 4849 * 0.23 + 4849 * 0.26 + 5825 * 0.3275 + 12645 * 0.37 + 12206 * 0.435 + 29202 * 0.45 + (taxableIncome - 81199) * 0.48;
  }

  breakdown.push({ name: "IRS (Income Tax)", amount: irs, percentage: annualSalary > 0 ? (irs / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += irs;

  // Social Security: 11%
  const socialSecurity = annualSalary * 0.11;
  breakdown.push({ name: "Social Security (Segurança Social)", amount: socialSecurity, percentage: 11, color: CHART_COLORS.social });
  totalTax += socialSecurity;

  return { breakdown, totalTax };
}

function calculateBelgiumTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // Social Security (ONSS): 13.07%
  const socialSecurity = annualSalary * 0.1307;
  breakdown.push({ name: "Social Security (ONSS)", amount: socialSecurity, percentage: 13.07, color: CHART_COLORS.social });
  totalTax += socialSecurity;

  // Taxable income = gross - SS - professional expenses deduction (€4,040) - personal allowance (€9,270)
  const netTaxable = Math.max(0, annualSalary - socialSecurity - 4040 - 9270);

  let federalTax = 0;
  if (netTaxable <= 15820) {
    federalTax = netTaxable * 0.25;
  } else if (netTaxable <= 27920) {
    federalTax = 15820 * 0.25 + (netTaxable - 15820) * 0.40;
  } else if (netTaxable <= 48320) {
    federalTax = 15820 * 0.25 + 12100 * 0.40 + (netTaxable - 27920) * 0.45;
  } else {
    federalTax = 15820 * 0.25 + 12100 * 0.40 + 20400 * 0.45 + (netTaxable - 48320) * 0.50;
  }

  // Communal tax: avg 7% on federal tax
  const communalTax = federalTax * 0.07;
  const totalIncomeTax = federalTax + communalTax;

  breakdown.push({ name: "Federal + Communal Tax", amount: totalIncomeTax, percentage: annualSalary > 0 ? (totalIncomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += totalIncomeTax;

  return { breakdown, totalTax };
}

function calculateAustriaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // Employee social contributions: KV 3.87% + PV 10.25% + AV 3% + WHB 0.5% = 17.62%
  const socialContributions = annualSalary * 0.1762;
  breakdown.push({ name: "Social Contributions (SV)", amount: socialContributions, percentage: 17.62, color: CHART_COLORS.social });
  totalTax += socialContributions;

  // ESt (income tax) on gross (Austria taxes gross before SS deduction for wage tax)
  let einkommensteuer = 0;
  if (annualSalary <= 12816) {
    einkommensteuer = 0;
  } else if (annualSalary <= 20818) {
    einkommensteuer = (annualSalary - 12816) * 0.20;
  } else if (annualSalary <= 34513) {
    einkommensteuer = 8002 * 0.20 + (annualSalary - 20818) * 0.30;
  } else if (annualSalary <= 66612) {
    einkommensteuer = 8002 * 0.20 + 13695 * 0.30 + (annualSalary - 34513) * 0.41;
  } else if (annualSalary <= 99266) {
    einkommensteuer = 8002 * 0.20 + 13695 * 0.30 + 32099 * 0.41 + (annualSalary - 66612) * 0.48;
  } else if (annualSalary <= 1000000) {
    einkommensteuer = 8002 * 0.20 + 13695 * 0.30 + 32099 * 0.41 + 32654 * 0.48 + (annualSalary - 99266) * 0.50;
  } else {
    einkommensteuer = 8002 * 0.20 + 13695 * 0.30 + 32099 * 0.41 + 32654 * 0.48 + 900734 * 0.50 + (annualSalary - 1000000) * 0.55;
  }

  breakdown.push({ name: "Income Tax (Lohnsteuer)", amount: einkommensteuer, percentage: annualSalary > 0 ? (einkommensteuer / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += einkommensteuer;

  return { breakdown, totalTax };
}

function calculatePolandTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // ZUS contributions: pension 9.76% + disability 1.5% + sickness 2.45% (cap PLN 234,720)
  const zusCap = 234720;
  const zusEmerytalna = Math.min(annualSalary, zusCap) * 0.0976;
  const zusRentowa = Math.min(annualSalary, zusCap) * 0.015;
  const zusChorobowa = annualSalary * 0.0245;
  const totalZUS = zusEmerytalna + zusRentowa + zusChorobowa;

  breakdown.push({ name: "ZUS (Social Insurance)", amount: totalZUS, percentage: annualSalary > 0 ? (totalZUS / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += totalZUS;

  // Health contribution (NFZ): 9% of (gross - ZUS)
  const healthBase = Math.max(0, annualSalary - totalZUS);
  const healthContribution = healthBase * 0.09;
  breakdown.push({ name: "Health Insurance (NFZ)", amount: healthContribution, percentage: annualSalary > 0 ? (healthContribution / annualSalary) * 100 : 0, color: CHART_COLORS.other });
  totalTax += healthContribution;

  // PIT: kwota wolna 30,000 PLN (free amount), 12% up to 120,000, 32% above
  const taxableIncome = Math.max(0, annualSalary - 30000);
  let pit = 0;
  if (taxableIncome <= 90000) {
    pit = taxableIncome * 0.12;
  } else {
    pit = 90000 * 0.12 + (taxableIncome - 90000) * 0.32;
  }

  breakdown.push({ name: "PIT (Income Tax)", amount: pit, percentage: annualSalary > 0 ? (pit / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += pit;

  return { breakdown, totalTax };
}

function calculateHongKongTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // Salaries Tax — personal allowance HKD 132,000
  const netChargeableIncome = Math.max(0, annualSalary - 132000);

  let progressiveTax = 0;
  if (netChargeableIncome <= 50000) {
    progressiveTax = netChargeableIncome * 0.02;
  } else if (netChargeableIncome <= 100000) {
    progressiveTax = 50000 * 0.02 + (netChargeableIncome - 50000) * 0.06;
  } else if (netChargeableIncome <= 150000) {
    progressiveTax = 50000 * 0.02 + 50000 * 0.06 + (netChargeableIncome - 100000) * 0.10;
  } else if (netChargeableIncome <= 200000) {
    progressiveTax = 50000 * 0.02 + 50000 * 0.06 + 50000 * 0.10 + (netChargeableIncome - 150000) * 0.14;
  } else {
    progressiveTax = 50000 * 0.02 + 50000 * 0.06 + 50000 * 0.10 + 50000 * 0.14 + (netChargeableIncome - 200000) * 0.17;
  }

  // Standard rate: 15% on net assessable income (after personal allowance)
  const standardTax = netChargeableIncome * 0.15;

  // Pay the lower of progressive or standard rate
  const salariesTax = Math.min(progressiveTax, standardTax);

  breakdown.push({ name: "Salaries Tax", amount: salariesTax, percentage: annualSalary > 0 ? (salariesTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += salariesTax;

  // MPF (Mandatory Provident Fund): 5% capped at HKD 18,000/year
  const mpf = Math.min(annualSalary * 0.05, 18000);
  breakdown.push({ name: "MPF (Mandatory Provident Fund)", amount: mpf, percentage: annualSalary > 0 ? (mpf / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += mpf;

  return { breakdown, totalTax };
}

function calculateSouthKoreaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  // Employment income deduction (근로소득공제)
  let employmentDeduction: number;
  if (annualSalary <= 5000000) {
    employmentDeduction = annualSalary * 0.70;
  } else if (annualSalary <= 15000000) {
    employmentDeduction = 3500000 + (annualSalary - 5000000) * 0.40;
  } else if (annualSalary <= 45000000) {
    employmentDeduction = 7500000 + (annualSalary - 15000000) * 0.15;
  } else if (annualSalary <= 100000000) {
    employmentDeduction = 12000000 + (annualSalary - 45000000) * 0.05;
  } else {
    employmentDeduction = Math.min(14750000 + (annualSalary - 100000000) * 0.02, 20000000);
  }

  // Personal deduction: 1,500,000 KRW
  const taxableIncome = Math.max(0, annualSalary - employmentDeduction - 1500000);

  // Income tax brackets
  let nationalTax = 0;
  if (taxableIncome <= 14000000) {
    nationalTax = taxableIncome * 0.06;
  } else if (taxableIncome <= 50000000) {
    nationalTax = 840000 + (taxableIncome - 14000000) * 0.15;
  } else if (taxableIncome <= 88000000) {
    nationalTax = 6240000 + (taxableIncome - 50000000) * 0.24;
  } else if (taxableIncome <= 150000000) {
    nationalTax = 15360000 + (taxableIncome - 88000000) * 0.35;
  } else if (taxableIncome <= 300000000) {
    nationalTax = 37060000 + (taxableIncome - 150000000) * 0.38;
  } else if (taxableIncome <= 500000000) {
    nationalTax = 94060000 + (taxableIncome - 300000000) * 0.40;
  } else {
    nationalTax = 174060000 + (taxableIncome - 500000000) * 0.42;
  }

  // Earned income tax credit (근로소득세액공제)
  let earnedIncomeCredit: number;
  if (nationalTax <= 730000) {
    earnedIncomeCredit = nationalTax * 0.55;
  } else {
    earnedIncomeCredit = 401500 + (nationalTax - 730000) * 0.30;
  }
  if (annualSalary <= 33000000) earnedIncomeCredit = Math.min(earnedIncomeCredit, 740000);
  else if (annualSalary <= 70000000) earnedIncomeCredit = Math.min(earnedIncomeCredit, 660000);
  else earnedIncomeCredit = Math.min(earnedIncomeCredit, 500000);

  const incomeTaxAfterCredit = Math.max(0, nationalTax - earnedIncomeCredit);
  const localIncomeTax = incomeTaxAfterCredit * 0.10;
  const totalIncomeTax = incomeTaxAfterCredit + localIncomeTax;

  breakdown.push({ name: "Income Tax + Local Surtax", amount: totalIncomeTax, percentage: annualSalary > 0 ? (totalIncomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += totalIncomeTax;

  // NPS (국민연금): 4.5% capped at KRW 74,040,000/year
  const nps = Math.min(annualSalary, 74040000) * 0.045;
  breakdown.push({ name: "National Pension (NPS)", amount: nps, percentage: annualSalary > 0 ? (nps / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += nps;

  // NHIS (건강보험 + 장기요양): ~4% total
  const healthInsurance = annualSalary * 0.03999;
  breakdown.push({ name: "Health Insurance (NHIS)", amount: healthInsurance, percentage: annualSalary > 0 ? (healthInsurance / annualSalary) * 100 : 0, color: CHART_COLORS.other });
  totalTax += healthInsurance;

  // Employment insurance: 0.9%
  const employmentInsurance = annualSalary * 0.009;
  breakdown.push({ name: "Employment Insurance", amount: employmentInsurance, percentage: 0.9, color: CHART_COLORS.other });
  totalTax += employmentInsurance;

  return { breakdown, totalTax };
return { breakdown, totalTax };
}
function calculateSaudiArabiaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];

  breakdown.push({
    name: "No Income Tax",
    amount: 0,
    percentage: 0,
    color: CHART_COLORS.net
  });

  // GOSI for expatriates: EMPLOYER pays 2% (occupational hazard only)
  // Employee contribution: 0% — expats do not pay into GOSI
  // So employee take-home is 100% of gross (no employee deductions)
  breakdown.push({ 
    name: "GOSI (Employer-paid: 2% Occupational Hazards)", 
    amount: 0, 
    percentage: 0, 
    color: CHART_COLORS.net 
  });

  return { breakdown, totalTax: 0 };
}
function calculateBrazilTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let irpf = 0;
  if (annualSalary <= 24511.92) {
    irpf = 0;
  } else if (annualSalary <= 33919.80) {
    irpf = (annualSalary - 24511.92) * 0.075;
  } else if (annualSalary <= 45012.60) {
    irpf = (33919.80 - 24511.92) * 0.075 + (annualSalary - 33919.80) * 0.15;
  } else if (annualSalary <= 55976.16) {
    irpf = (33919.80 - 24511.92) * 0.075 + (45012.60 - 33919.80) * 0.15 + (annualSalary - 45012.60) * 0.225;
  } else {
    irpf = (33919.80 - 24511.92) * 0.075 + (45012.60 - 33919.80) * 0.15 + (55976.16 - 45012.60) * 0.225 + (annualSalary - 55976.16) * 0.275;
  }
  breakdown.push({ name: "IRPF (Income Tax)", amount: irpf, percentage: annualSalary > 0 ? (irpf / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += irpf;

  let inss = 0;
  if (annualSalary <= 18216) {
    inss = annualSalary * 0.075;
  } else if (annualSalary <= 33526.56) {
    inss = 1366.20 + (annualSalary - 18216) * 0.09;
  } else if (annualSalary <= 50289.96) {
    inss = 2744.15 + (annualSalary - 33526.56) * 0.12;
  } else if (annualSalary <= 97888.92) {
    inss = 4755.76 + (annualSalary - 50289.96) * 0.14;
  } else {
    inss = 11419.61;
  }
  breakdown.push({ name: "INSS (Social Security)", amount: inss, percentage: annualSalary > 0 ? (inss / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += inss;

  return { breakdown, totalTax };
}

function calculateMexicoTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const brackets = [
    { limit: 8952.49, base: 0, rate: 0.0192, fixedFee: 0 },
    { limit: 21036.44, base: 8952.49, rate: 0.064, fixedFee: 171.88 },
    { limit: 36973.09, base: 21036.44, rate: 0.1088, fixedFee: 945.31 },
    { limit: 43008.34, base: 36973.09, rate: 0.16, fixedFee: 2677.77 },
    { limit: 51519.94, base: 43008.34, rate: 0.1792, fixedFee: 3644.33 },
    { limit: 104213.84, base: 51519.94, rate: 0.2136, fixedFee: 5166.38 },
    { limit: 165860.49, base: 104213.84, rate: 0.2352, fixedFee: 16415.41 },
    { limit: 315070.20, base: 165860.49, rate: 0.30, fixedFee: 30906.69 },
    { limit: 420093.20, base: 315070.20, rate: 0.32, fixedFee: 75587.76 },
    { limit: 630139.86, base: 420093.20, rate: 0.34, fixedFee: 109196.40 },
    { limit: Infinity, base: 630139.86, rate: 0.35, fixedFee: 180850.82 },
  ];
  let isr = 0;
  for (const b of brackets) {
    if (annualSalary <= b.limit) { isr = b.fixedFee + (annualSalary - b.base) * b.rate; break; }
  }
  breakdown.push({ name: "ISR (Income Tax)", amount: isr, percentage: annualSalary > 0 ? (isr / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += isr;

  const imss = annualSalary * 0.02125;
  breakdown.push({ name: "IMSS (Social Security)", amount: imss, percentage: annualSalary > 0 ? (imss / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += imss;

  return { breakdown, totalTax };
}

function calculateIndiaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let incomeTax = 0;
  if (annualSalary <= 300000) {
    incomeTax = 0;
  } else if (annualSalary <= 700000) {
    incomeTax = (annualSalary - 300000) * 0.05;
  } else if (annualSalary <= 1000000) {
    incomeTax = 400000 * 0.05 + (annualSalary - 700000) * 0.10;
  } else if (annualSalary <= 1200000) {
    incomeTax = 400000 * 0.05 + 300000 * 0.10 + (annualSalary - 1000000) * 0.15;
  } else if (annualSalary <= 1500000) {
    incomeTax = 400000 * 0.05 + 300000 * 0.10 + 200000 * 0.15 + (annualSalary - 1200000) * 0.20;
  } else {
    incomeTax = 400000 * 0.05 + 300000 * 0.10 + 200000 * 0.15 + 300000 * 0.20 + (annualSalary - 1500000) * 0.30;
  }
  if (annualSalary <= 700000) incomeTax = 0;
  const cess = incomeTax * 0.04;
  const totalIncomeTax = incomeTax + cess;

  breakdown.push({ name: "Income Tax + Cess (4%)", amount: totalIncomeTax, percentage: annualSalary > 0 ? (totalIncomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += totalIncomeTax;

  const epf = Math.min(annualSalary * 0.40 * 0.12, 21600);
  breakdown.push({ name: "EPF (Employee Provident Fund)", amount: epf, percentage: annualSalary > 0 ? (epf / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += epf;

  return { breakdown, totalTax };
}

function calculateFinlandTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let stateTax = 0;
  if (annualSalary <= 21200) {
    stateTax = 0;
  } else if (annualSalary <= 31500) {
    stateTax = (annualSalary - 21200) * 0.1264;
  } else if (annualSalary <= 52200) {
    stateTax = (31500 - 21200) * 0.1264 + (annualSalary - 31500) * 0.1984;
  } else if (annualSalary <= 88700) {
    stateTax = (31500 - 21200) * 0.1264 + (52200 - 31500) * 0.1984 + (annualSalary - 52200) * 0.2595;
  } else {
    stateTax = (31500 - 21200) * 0.1264 + (52200 - 31500) * 0.1984 + (88700 - 52200) * 0.2595 + (annualSalary - 88700) * 0.3199;
  }
  breakdown.push({ name: "State Income Tax", amount: stateTax, percentage: annualSalary > 0 ? (stateTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += stateTax;

  const municipalTax = annualSalary * 0.212;
  breakdown.push({ name: "Municipal Tax (21.2%)", amount: municipalTax, percentage: 21.2, color: CHART_COLORS.state });
  totalTax += municipalTax;

  const pension = annualSalary * 0.0715;
  breakdown.push({ name: "Pension (TyEL)", amount: pension, percentage: 7.15, color: CHART_COLORS.social });
  totalTax += pension;

  const unemployment = annualSalary * 0.015;
  breakdown.push({ name: "Unemployment Insurance (1.5%)", amount: unemployment, percentage: 1.5, color: CHART_COLORS.other });
  totalTax += unemployment;

  const health = annualSalary * 0.0196;
  breakdown.push({ name: "Health Insurance", amount: health, percentage: 1.96, color: CHART_COLORS.other });
  totalTax += health;

  return { breakdown, totalTax };
}

function calculateCzechRepublicTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const threshold = 4709904;
  let incomeTax = annualSalary <= threshold ? annualSalary * 0.15 : threshold * 0.15 + (annualSalary - threshold) * 0.23;
  incomeTax = Math.max(0, incomeTax - 30840);

  breakdown.push({ name: "Income Tax (Daň z příjmu)", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  const socialSecurity = annualSalary * 0.065;
  breakdown.push({ name: "Social Security", amount: socialSecurity, percentage: 6.5, color: CHART_COLORS.social });
  totalTax += socialSecurity;

  const healthInsurance = annualSalary * 0.045;
  breakdown.push({ name: "Health Insurance", amount: healthInsurance, percentage: 4.5, color: CHART_COLORS.other });
  totalTax += healthInsurance;

  return { breakdown, totalTax };
}

function calculateHungaryTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const incomeTax = annualSalary * 0.15;
  breakdown.push({ name: "Personal Income Tax (SZJA)", amount: incomeTax, percentage: 15, color: CHART_COLORS.income });
  totalTax += incomeTax;

  const socialContribution = annualSalary * 0.185;
  breakdown.push({ name: "Social Contributions", amount: socialContribution, percentage: 18.5, color: CHART_COLORS.social });
  totalTax += socialContribution;

  return { breakdown, totalTax };
}

function calculateGreeceTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let incomeTax = 0;
  if (annualSalary <= 10000) {
    incomeTax = annualSalary * 0.09;
  } else if (annualSalary <= 20000) {
    incomeTax = 900 + (annualSalary - 10000) * 0.22;
  } else if (annualSalary <= 30000) {
    incomeTax = 3100 + (annualSalary - 20000) * 0.28;
  } else if (annualSalary <= 40000) {
    incomeTax = 5900 + (annualSalary - 30000) * 0.36;
  } else {
    incomeTax = 9500 + (annualSalary - 40000) * 0.44;
  }
  breakdown.push({ name: "Income Tax (Φόρος Εισοδήματος)", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  const ika = annualSalary * 0.1387;
  breakdown.push({ name: "IKA (Social Insurance)", amount: ika, percentage: 13.87, color: CHART_COLORS.social });
  totalTax += ika;

  return { breakdown, totalTax };
}

function calculateQatarTax(_annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  return { breakdown: [{ name: "No Income Tax", amount: 0, percentage: 0, color: CHART_COLORS.net }], totalTax: 0 };
}

function calculateBahrainTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  breakdown.push({ name: "No Income Tax", amount: 0, percentage: 0, color: CHART_COLORS.net });
  const sio = annualSalary * 0.01;
  breakdown.push({ name: "SIO (Social Insurance)", amount: sio, percentage: 1, color: CHART_COLORS.social });
  return { breakdown, totalTax: sio };
}

function calculateKuwaitTax(_annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  return { breakdown: [{ name: "No Income Tax", amount: 0, percentage: 0, color: CHART_COLORS.net }], totalTax: 0 };
}

function calculateOmanTax(_annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  return { breakdown: [{ name: "No Income Tax", amount: 0, percentage: 0, color: CHART_COLORS.net }], totalTax: 0 };
}

function calculateThailandTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const expenseDeduction = Math.min(annualSalary * 0.50, 100000);
  const taxableIncome = Math.max(0, annualSalary - expenseDeduction - 60000);

  let incomeTax = 0;
  if (taxableIncome <= 150000) {
    incomeTax = 0;
  } else if (taxableIncome <= 300000) {
    incomeTax = (taxableIncome - 150000) * 0.05;
  } else if (taxableIncome <= 500000) {
    incomeTax = 7500 + (taxableIncome - 300000) * 0.10;
  } else if (taxableIncome <= 750000) {
    incomeTax = 27500 + (taxableIncome - 500000) * 0.15;
  } else if (taxableIncome <= 1000000) {
    incomeTax = 65000 + (taxableIncome - 750000) * 0.20;
  } else if (taxableIncome <= 2000000) {
    incomeTax = 115000 + (taxableIncome - 1000000) * 0.25;
  } else if (taxableIncome <= 5000000) {
    incomeTax = 365000 + (taxableIncome - 2000000) * 0.30;
  } else {
    incomeTax = 1265000 + (taxableIncome - 5000000) * 0.35;
  }
  breakdown.push({ name: "Income Tax", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  const socialSecurity = Math.min(annualSalary * 0.05, 9000);
  breakdown.push({ name: "Social Security (SSO)", amount: socialSecurity, percentage: annualSalary > 0 ? (socialSecurity / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += socialSecurity;

  return { breakdown, totalTax };
}

function calculateMalaysiaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const epf = annualSalary * 0.11;
  breakdown.push({ name: "EPF (Employee Provident Fund)", amount: epf, percentage: 11, color: CHART_COLORS.social });
  totalTax += epf;

  const taxableIncome = Math.max(0, annualSalary - 9000);
  let incomeTax = 0;
  if (taxableIncome <= 5000) {
    incomeTax = 0;
  } else if (taxableIncome <= 20000) {
    incomeTax = (taxableIncome - 5000) * 0.01;
  } else if (taxableIncome <= 35000) {
    incomeTax = 150 + (taxableIncome - 20000) * 0.03;
  } else if (taxableIncome <= 50000) {
    incomeTax = 600 + (taxableIncome - 35000) * 0.08;
  } else if (taxableIncome <= 70000) {
    incomeTax = 1800 + (taxableIncome - 50000) * 0.13;
  } else if (taxableIncome <= 100000) {
    incomeTax = 4400 + (taxableIncome - 70000) * 0.21;
  } else if (taxableIncome <= 400000) {
    incomeTax = 10700 + (taxableIncome - 100000) * 0.24;
  } else if (taxableIncome <= 600000) {
    incomeTax = 82700 + (taxableIncome - 400000) * 0.245;
  } else if (taxableIncome <= 2000000) {
    incomeTax = 131700 + (taxableIncome - 600000) * 0.25;
  } else {
    incomeTax = 481700 + (taxableIncome - 2000000) * 0.30;
  }
  breakdown.push({ name: "Income Tax", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  const socso = Math.min(annualSalary * 0.005, 1200);
  breakdown.push({ name: "SOCSO", amount: socso, percentage: annualSalary > 0 ? (socso / annualSalary) * 100 : 0, color: CHART_COLORS.other });
  totalTax += socso;

  return { breakdown, totalTax };
}

function calculateIndonesiaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const taxableIncome = Math.max(0, annualSalary - 54000000);
  let incomeTax = 0;
  if (taxableIncome <= 60000000) {
    incomeTax = taxableIncome * 0.05;
  } else if (taxableIncome <= 250000000) {
    incomeTax = 3000000 + (taxableIncome - 60000000) * 0.15;
  } else if (taxableIncome <= 500000000) {
    incomeTax = 31500000 + (taxableIncome - 250000000) * 0.25;
  } else if (taxableIncome <= 5000000000) {
    incomeTax = 94000000 + (taxableIncome - 500000000) * 0.30;
  } else {
    incomeTax = 1444000000 + (taxableIncome - 5000000000) * 0.35;
  }
  breakdown.push({ name: "PPh 21 (Income Tax)", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  // BPJS Kesehatan (Health): 1% employee, capped at IDR 12,000,000/year (IDR 1M/month ceiling)
  const bpjsHealth = Math.min(annualSalary * 0.01, 12000000);
  if (bpjsHealth > 0) {
    breakdown.push({ name: "BPJS Kesehatan (Health)", amount: bpjsHealth, percentage: annualSalary > 0 ? (bpjsHealth / annualSalary) * 100 : 0, color: CHART_COLORS.social });
    totalTax += bpjsHealth;
  }

  // BPJS Ketenagakerjaan (JHT): 2% employee, capped at IDR 18,208,800/year (ceiling ~IDR 9.1M/month)
  const bpjsWork = Math.min(annualSalary * 0.02, 18208800);
  if (bpjsWork > 0) {
    breakdown.push({ name: "BPJS Ketenagakerjaan (JHT 2%)", amount: bpjsWork, percentage: annualSalary > 0 ? (bpjsWork / annualSalary) * 100 : 0, color: CHART_COLORS.other });
    totalTax += bpjsWork;
  }

  return { breakdown, totalTax };
}

function calculateSouthAfricaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let incomeTax = 0;
  if (annualSalary <= 237100) {
    incomeTax = annualSalary * 0.18;
  } else if (annualSalary <= 370500) {
    incomeTax = 42678 + (annualSalary - 237100) * 0.26;
  } else if (annualSalary <= 512800) {
    incomeTax = 77362 + (annualSalary - 370500) * 0.31;
  } else if (annualSalary <= 673000) {
    incomeTax = 121475 + (annualSalary - 512800) * 0.36;
  } else if (annualSalary <= 857900) {
    incomeTax = 179147 + (annualSalary - 673000) * 0.39;
  } else if (annualSalary <= 1817000) {
    incomeTax = 251258 + (annualSalary - 857900) * 0.41;
  } else {
    incomeTax = 644489 + (annualSalary - 1817000) * 0.45;
  }
  incomeTax = Math.max(0, incomeTax - 17235);
  breakdown.push({ name: "Income Tax (SARS)", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  const uif = Math.min(annualSalary * 0.01, 2125.44);
  breakdown.push({ name: "UIF (Unemployment Insurance)", amount: uif, percentage: annualSalary > 0 ? (uif / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += uif;

  return { breakdown, totalTax };
}

function calculateNigeriaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const cra = Math.max(200000, annualSalary * 0.01) + annualSalary * 0.20;
  const taxableIncome = Math.max(0, annualSalary - cra);

  let paye = 0;
  if (taxableIncome <= 300000) {
    paye = taxableIncome * 0.07;
  } else if (taxableIncome <= 600000) {
    paye = 21000 + (taxableIncome - 300000) * 0.11;
  } else if (taxableIncome <= 1100000) {
    paye = 54000 + (taxableIncome - 600000) * 0.15;
  } else if (taxableIncome <= 1600000) {
    paye = 129000 + (taxableIncome - 1100000) * 0.19;
  } else if (taxableIncome <= 3200000) {
    paye = 224000 + (taxableIncome - 1600000) * 0.21;
  } else {
    paye = 560000 + (taxableIncome - 3200000) * 0.24;
  }
  breakdown.push({ name: "PAYE (Income Tax)", amount: paye, percentage: annualSalary > 0 ? (paye / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += paye;

  const pension = annualSalary * 0.08;
  breakdown.push({ name: "Pension (PenCom)", amount: pension, percentage: 8, color: CHART_COLORS.social });
  totalTax += pension;

  return { breakdown, totalTax };
}

function calculateKenyaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let paye = 0;
  if (annualSalary <= 288000) {
    paye = annualSalary * 0.10;
  } else if (annualSalary <= 388000) {
    paye = 28800 + (annualSalary - 288000) * 0.25;
  } else if (annualSalary <= 6000000) {
    paye = 53800 + (annualSalary - 388000) * 0.30;
  } else if (annualSalary <= 9600000) {
    paye = 1737400 + (annualSalary - 6000000) * 0.325;
  } else {
    paye = 2907400 + (annualSalary - 9600000) * 0.35;
  }
  paye = Math.max(0, paye - 28800);
  breakdown.push({ name: "PAYE (Income Tax)", amount: paye, percentage: annualSalary > 0 ? (paye / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += paye;

  const shif = annualSalary * 0.0275;
  breakdown.push({ name: "SHIF (Health Insurance)", amount: shif, percentage: 2.75, color: CHART_COLORS.social });
  totalTax += shif;

  const nssf = Math.min(annualSalary * 0.06, 25920);
  breakdown.push({ name: "NSSF (Pension)", amount: nssf, percentage: annualSalary > 0 ? (nssf / annualSalary) * 100 : 0, color: CHART_COLORS.other });
  totalTax += nssf;

  return { breakdown, totalTax };
}

function calculateIsraelTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let incomeTax = 0;
  if (annualSalary <= 81480) {
    incomeTax = annualSalary * 0.10;
  } else if (annualSalary <= 116760) {
    incomeTax = 8148 + (annualSalary - 81480) * 0.14;
  } else if (annualSalary <= 187800) {
    incomeTax = 13087.2 + (annualSalary - 116760) * 0.20;
  } else if (annualSalary <= 261120) {
    incomeTax = 27295.2 + (annualSalary - 187800) * 0.31;
  } else if (annualSalary <= 542160) {
    incomeTax = 50023.4 + (annualSalary - 261120) * 0.35;
  } else if (annualSalary <= 698280) {
    incomeTax = 148387.4 + (annualSalary - 542160) * 0.47;
  } else {
    incomeTax = 221764.6 + (annualSalary - 698280) * 0.50;
  }
  incomeTax = Math.max(0, incomeTax - 6534);
  breakdown.push({ name: "Income Tax", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  const niLowCap = 81180;
  const niHighCap = 156264;
  let nationalInsurance = 0;
  if (annualSalary <= niLowCap) {
    nationalInsurance = annualSalary * 0.035;
  } else if (annualSalary <= niHighCap) {
    nationalInsurance = niLowCap * 0.035 + (annualSalary - niLowCap) * 0.07;
  } else {
    nationalInsurance = niLowCap * 0.035 + (niHighCap - niLowCap) * 0.07;
  }
  breakdown.push({ name: "National Insurance (BI)", amount: nationalInsurance, percentage: annualSalary > 0 ? (nationalInsurance / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += nationalInsurance;

  const hiLowCap = 90264;
  const healthInsurance = annualSalary <= hiLowCap ? annualSalary * 0.031 : hiLowCap * 0.031 + (annualSalary - hiLowCap) * 0.05;
  breakdown.push({ name: "Health Insurance", amount: healthInsurance, percentage: annualSalary > 0 ? (healthInsurance / annualSalary) * 100 : 0, color: CHART_COLORS.other });
  totalTax += healthInsurance;

  return { breakdown, totalTax };
}

function calculateRussiaTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  const incomeTax = annualSalary <= 5000000 ? annualSalary * 0.13 : 650000 + (annualSalary - 5000000) * 0.15;
  breakdown.push({ name: "Income Tax (НДФЛ)", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  const socialCap = 2225000;
  const social = Math.min(annualSalary, socialCap) * 0.22;
  breakdown.push({ name: "Social Insurance (22%, capped)", amount: social, percentage: annualSalary > 0 ? (social / annualSalary) * 100 : 0, color: CHART_COLORS.social });
  totalTax += social;

  const healthCap = 1966278;
  const health = Math.min(annualSalary, healthCap) * 0.051;
  breakdown.push({ name: "Health Insurance (5.1%, capped)", amount: health, percentage: annualSalary > 0 ? (health / annualSalary) * 100 : 0, color: CHART_COLORS.other });
  totalTax += health;

  return { breakdown, totalTax };
}

function calculateTurkeyTax(annualSalary: number): { breakdown: TaxBreakdownItem[], totalTax: number } {
  const breakdown: TaxBreakdownItem[] = [];
  let totalTax = 0;

  let incomeTax = 0;
  if (annualSalary <= 110000) {
    incomeTax = annualSalary * 0.15;
  } else if (annualSalary <= 230000) {
    incomeTax = 16500 + (annualSalary - 110000) * 0.20;
  } else if (annualSalary <= 870000) {
    incomeTax = 40500 + (annualSalary - 230000) * 0.27;
  } else if (annualSalary <= 3000000) {
    incomeTax = 213300 + (annualSalary - 870000) * 0.35;
  } else {
    incomeTax = 958800 + (annualSalary - 3000000) * 0.40;
  }
  breakdown.push({ name: "Income Tax (Gelir Vergisi)", amount: incomeTax, percentage: annualSalary > 0 ? (incomeTax / annualSalary) * 100 : 0, color: CHART_COLORS.income });
  totalTax += incomeTax;

  const sgk = annualSalary * 0.14;
  breakdown.push({ name: "SGK (Social Security)", amount: sgk, percentage: 14, color: CHART_COLORS.social });
  totalTax += sgk;

  const unemployment = annualSalary * 0.01;
  breakdown.push({ name: "Unemployment Insurance", amount: unemployment, percentage: 1, color: CHART_COLORS.other });
  totalTax += unemployment;

  return { breakdown, totalTax };
}

export function calculateTax(
  annualSalary: number,
  countryCode: CountryCode,
  stateCode?: string
): { breakdown: TaxBreakdownItem[], totalTax: number } {
  switch (countryCode) {
    case "uk":
      return calculateUKTax(annualSalary);
    case "us":
      return calculateUSTax(annualSalary, stateCode || "CA");
    case "ca":
      return calculateCanadaTax(annualSalary);
    case "au":
      return calculateAustraliaTax(annualSalary);
    case "de":
      return calculateGermanyTax(annualSalary);
    case "nl":
      return calculateNetherlandsTax(annualSalary);
    case "ae":
      return calculateUAETax(annualSalary);
    case "sg":
      return calculateSingaporeTax(annualSalary);
    case "ch":
      return calculateSwitzerlandTax(annualSalary);
    case "ie":
      return calculateIrelandTax(annualSalary);
    case "fr":
      return calculateFranceTax(annualSalary);
    case "es":
      return calculateSpainTax(annualSalary);
    case "it":
      return calculateItalyTax(annualSalary);
    case "se":
      return calculateSwedenTax(annualSalary);
    case "no":
      return calculateNorwayTax(annualSalary);
    case "dk":
      return calculateDenmarkTax(annualSalary);
    case "nz":
      return calculateNewZealandTax(annualSalary);
    case "jp":
      return calculateJapanTax(annualSalary);
    case "pt":
      return calculatePortugalTax(annualSalary);
    case "be":
      return calculateBelgiumTax(annualSalary);
    case "at":
      return calculateAustriaTax(annualSalary);
    case "pl":
      return calculatePolandTax(annualSalary);
    case "hk":
      return calculateHongKongTax(annualSalary);
    case "kr":
      return calculateSouthKoreaTax(annualSalary);
    case "sa":
      return calculateSaudiArabiaTax(annualSalary);
    case "br":
      return calculateBrazilTax(annualSalary);
    case "mx":
      return calculateMexicoTax(annualSalary);
    case "in":
      return calculateIndiaTax(annualSalary);
    case "fi":
      return calculateFinlandTax(annualSalary);
    case "cz":
      return calculateCzechRepublicTax(annualSalary);
    case "hu":
      return calculateHungaryTax(annualSalary);
    case "gr":
      return calculateGreeceTax(annualSalary);
    case "qa":
      return calculateQatarTax(annualSalary);
    case "bh":
      return calculateBahrainTax(annualSalary);
    case "kw":
      return calculateKuwaitTax(annualSalary);
    case "om":
      return calculateOmanTax(annualSalary);
    case "th":
      return calculateThailandTax(annualSalary);
    case "my":
      return calculateMalaysiaTax(annualSalary);
    case "id":
      return calculateIndonesiaTax(annualSalary);
    case "za":
      return calculateSouthAfricaTax(annualSalary);
    case "ng":
      return calculateNigeriaTax(annualSalary);
    case "ke":
      return calculateKenyaTax(annualSalary);
    case "il":
      return calculateIsraelTax(annualSalary);
    case "ru":
      return calculateRussiaTax(annualSalary);
    case "tr":
      return calculateTurkeyTax(annualSalary);
    default:
      return { breakdown: [], totalTax: 0 };
  }
}

export function useTaxCalculator(
  salary: number,
  payPeriod: PayPeriod,
  countryCode: CountryCode,
  stateCode: string
): TaxCalculationResult | null {
  return useMemo(() => {
    if (salary <= 0) return null;

    const country = countries.find(c => c.code === countryCode);
    if (!country) return null;

    const multiplier = payPeriodMultipliers[payPeriod];
    const annualSalary = salary * multiplier;

    const { breakdown, totalTax } = calculateTax(annualSalary, countryCode, stateCode);

    return {
      grossSalary: annualSalary,
      netSalary: annualSalary - totalTax,
      totalTax,
      effectiveTaxRate: annualSalary > 0 ? (totalTax / annualSalary) * 100 : 0,
      breakdown,
      country,
      payPeriod: "yearly"
    };
  }, [salary, payPeriod, countryCode, stateCode]);
}

export function useCountryComparison(
  salary: number,
  payPeriod: PayPeriod,
  stateCode: string
): CountryComparisonResult[] {
  return useMemo(() => {
    if (salary <= 0) return [];

    const multiplier = payPeriodMultipliers[payPeriod];
    const annualSalary = salary * multiplier;

    return countries.map(country => {
      const { totalTax } = calculateTax(annualSalary, country.code, stateCode);
      const netSalary = annualSalary - totalTax;
      
      return {
        country,
        grossSalary: annualSalary,
        netSalary,
        totalTax,
        effectiveTaxRate: annualSalary > 0 ? (totalTax / annualSalary) * 100 : 0,
        difference: 0
      };
    }).sort((a, b) => b.netSalary - a.netSalary);
  }, [salary, payPeriod, stateCode]);
}
